// src/components/ToastProvider.jsx
import React from 'react';
import { Toaster } from 'react-hot-toast';
import { CheckCircle2, XCircle, Loader2, Info, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ToastProvider({ children }) {
  // Animated icon with bounce effect and color coding
  const AnimatedIcon = ({ icon: Icon, spin = false, type = 'default' }) => {
    // Icon colors based on toast type
    const iconColors = {
      success: '#10B981',   // Emerald green
      error: '#EF4444',     // Red
      loading: '#3B82F6',   // Blue
      info: '#8B5CF6',      // Violet
      warning: '#F59E0B'    // Amber
    };

    return (
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ 
          scale: [0, 1.2, 1],
          rotate: spin ? [0, 360] : 0
        }}
        transition={{ 
          duration: 0.6,
          rotate: spin ? { repeat: Infinity, duration: 1, ease: 'linear' } : { duration: 0.4 },
          scale: { times: [0, 0.7, 1], duration: 0.6 }
        }}
      >
        <Icon 
          size={22} 
          color={iconColors[type] || '#6B7280'} 
        />
      </motion.div>
    );
  };

  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        containerStyle={{
          zIndex: 9999999999,  // Ensure toasts appear above all other content
        }}
        toastOptions={{
          // Increased height and better styling
          duration: 4000,
          style: {
            padding: '16px 20px',
            minHeight: '60px',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: '#FFF',
            color: '#1F2937',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #E5E7EB',
            maxWidth: '450px',
          },
          
          // Default loading icon
          icon: <AnimatedIcon icon={Loader2} spin={true} type="loading" />,
          
          // Success toast
          success: {
            icon: <AnimatedIcon icon={CheckCircle2} type="success" />,
            style: {
              borderLeft: '4px solid #10B981',
            },
          },
          
          // Error toast
          error: {
            icon: <AnimatedIcon icon={XCircle} type="error" />,
            style: {
              borderLeft: '4px solid #EF4444',
            },
          },
          
          // Info toast
          custom: {
            icon: <AnimatedIcon icon={Info} type="info" />,
            style: {
              borderLeft: '4px solid #8B5CF6',
            },
          },
          
          // Warning toast
          blank: {
            icon: <AnimatedIcon icon={AlertTriangle} type="warning" />,
            style: {
              borderLeft: '4px solid #F59E0B',
            },
          },
          
          // Bounce animation for toast container
          className: 'toast-bounce-animation',
        }}
      />
      {children}
    </>
  );
}